import { assertAttendanceAccess } from 'v3/pages/ControlTower/Kanban/Attendance/access';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import Modal from 'v3/components/Modal';
import Select from 'v3/components/Select';
import InputAddress from 'v3/components/InputAddress';
import Input from 'v3/components/Input';
import DatePicker from 'v3/components/DatePicker';
import Button from 'v3/components/Button';
import Text from 'v3/components/Text';
import { useSnackbar } from 'v3/components/Snackbar';
import { useTravel } from 'v3/pages/Travel';
import { currencyOptions, currencyWithSymbol, makeConversion } from 'v3/utils/formatter';
import api, { cooperplaceApi } from 'services/api';
import { fetchCountries } from 'services/api/Locations';
import { fetchCities } from 'utils/fetches';
import moment from 'moment';
import { formatCurrency } from 'utils/formatter';
import { addressSchema } from './schema';

async function fetchProvinces(search, uf, country) {
  try {
    const response = await api.get('cities', {
      params: { search, uf, country },
    });
    return response.data;
  } catch (ex) {
    return [];
  }
}

async function fetchSelectOptions(url, params = {}) {
  try {
    const { data } = await api.get(url, { params });

    return data;
  } catch (err) {
    return [];
  }
}

function Address({ countries, errors, address, onChange }) {
  const [disableNumber, setDisableNumber] = useState(false);
  const [disableZipCode, setDisableZipCode] = useState(false);
  const [disableCity, setDisableCity] = useState(false);
  const [disableProvince, setDisableProvince] = useState(false);
  const [disableNeighborhood, setDisableNeighborhood] = useState(false);

  function handleFullAddressChange(key, value) {
    onChange({
      ...address,
      [key]: value,
    });
  }

  function disableAddress(place, city) {
    setDisableNumber(!!place?.number);
    setDisableZipCode(!!place?.zip_code);
    setDisableCity(!!city);
    setDisableProvince(!!city?.province);
    setDisableNeighborhood(!!place?.neighborhood);
  }

  useEffect(() => {
    setDisableNumber(!!address?.number);
    setDisableZipCode(!!address?.zipCode);
    setDisableCity(!!address?.city);
    setDisableProvince(!!address?.province);
    setDisableNeighborhood(!!address?.neighborhood);
  }, []);

  async function onPlaceSelected(place) {
    let { complement } = place;
    const [city] = await fetchProvinces(place.city, place.province, place.country_abbreviation);
    if (place.name && place.isEstablishment) {
      complement = place.name;
    }

    const contry = countries.find(
      c => c.abbreviation === place.country_abbreviation
    );

    onChange({
      ...address,
      country: contry,
      address: place.address,
      number: place.number,
      complement: complement || '',
      neighborhood: place.neighborhood,
      zipCode: place.zip_code,
      city,
      province: city?.province?.uf,
      lat: place?.lat,
      lng: place?.lng,
    });

    disableAddress(place, city);
  }

  function onChangeCity(city, options) {
    if (!city || options.action === 'create-option') {
      onChange({
        ...address,
        city: null,
        province: '',
      });
    }

    onChange({
      ...address,
      city,
      province: city?.province?.uf,
    });
  }

  function onChangeClient(client) {
    onChange({
      ...address,
      client,
    });
  }

  function onChangeCountry(value) {
    onChange({
      ...address,
      country: value,
    });
  }

  function onChangeAddress(event) {
    onChange({
      ...address,
      address: event?.target?.value,
      number: '',
      complement: '',
      neighborhood: '',
      zipCode: '',
      lat: null,
      lng: null,
      city: '',
      province: '',
      country: '',
    });
  }

  return (
    <>
      <Row className="px-2 mb-2">
        <Col md={12} xs={12} className="mt-2">
          <Select.Async
            label="Cliente"
            onSearch={(search) => fetchSelectOptions('persons/customers', { search })}
            value={address.client}
            getOptionLabel={(option) => {
              let label = `${option?.social_name} - ${option?.cgccpf}`;

              if (option.city) {
                label += ` - ${option?.city}`;
              }

              return label;
            }}
            getOptionValue={(option) => option?.id}
            onChange={onChangeClient}
            id="select-cliente"
          />
        </Col>
      </Row>
      <Row className="px-2">
        <Col xs={12} className="mb-2">
          <Select
            label="País *"
            options={countries}
            value={address.country}
            onChange={onChangeCountry}
            getOptionLabel={option => option.name}
            getOptionValue={option => option.id}
            error={errors?.country}
            disableClear
            id="select-modal-endereco-pais"
          />
        </Col>
        <Col xs={8} className="mb-2">
          <InputAddress
            label="Logradouro *"
            placeholder="Digite seu endereço"
            country={address?.country?.abbreviation}
            types={[]}
            onChange={onChangeAddress}
            onSelectedAddress={onPlaceSelected}
            value={address?.address}
            error={errors.address}
            id="input-modal-endereco-logradouro"
          />
        </Col>
        <Col md={4} xs={6} className="mb-2">
          <Input
            label="Número"
            disabled={disableNumber}
            value={address?.number}
            onChange={(event) => handleFullAddressChange('number', event.target.value)}
            id="input-modal-endereco-numero"
          />
        </Col>
        <Col md={4} xs={6} className="mb-2">
          <Input
            label="Complemento"
            value={address?.complement}
            onChange={(event) => handleFullAddressChange('complement', event.target.value)}
            id="input-modal-endereco-complemento"
          />
        </Col>
        <Col md={4} xs={12} className="mb-2">
          <Input
            label="Bairro"
            disabled={disableNeighborhood}
            value={address?.neighborhood}
            onChange={(event) => handleFullAddressChange('neighborhood', event.target.value)}
            id="input-modal-endereco-bairro"
          />
        </Col>
        <Col md={4} xs={12} className="mb-2">
          <Input
            label="CEP"
            disabled={disableZipCode}
            value={address?.zipCode}
            onChange={(event) => handleFullAddressChange('zipCode', event.target.value)}
            id="input-modal-endereco-cep"
          />
        </Col>
        <Col xs={8} className="mb-2">
          <Select.Async
            label="Cidade *"
            onSearch={(city) => fetchCities(city, '', address?.country?.abbreviation)}
            value={address?.city}
            horizontal
            creatable
            onChange={onChangeCity}
            getOptionLabel={(option) =>
              `${`${option.name} ${option.province?.uf ? `- ${option.province?.uf}` : ''}`}`
            }
            getOptionValue={(option) =>
              `${`${option.name} ${option.province?.uf ? `- ${option.province?.uf}` : ''}`}`
            }
            error={errors.city}
            isDisabled={disableCity}
            id="select-modal-endereco-cidade"
          />
        </Col>
        <Col md={4} xs={12} className="mb-2">
          <Input
            label="UF *"
            disabled={disableProvince}
            value={address?.province}
            onChange={(event) => handleFullAddressChange('province', event.target.value)}
            error={errors.province}
            id="input-modal-endereco-uf"
          />
        </Col>
        <Col md={6} className="mb-2">
          <Input
            label="Latitude"
            disabled
            value={address?.lat}
            error={errors?.lat}
            tooltip="Selecione um endereço sugerido para obter dados de latitude e longitude"
            id="input-modal-endereco-latitude"
          />
        </Col>
        <Col md={6} className="mb-2">
          <Input
            label="Longitude"
            disabled
            value={address?.lng}
            error={errors?.lng}
            tooltip="Selecione um endereço sugerido para obter dados de latitude e longitude"
            id="input-modal-endereco-longitude"
          />
        </Col>
        <Col md={6} xs={12} className="mb-2">
          <DatePicker
            guide
            label="Data inicio Troca Nota *"
            withTime
            value={address?.startSchedule}
            onChange={(event) => handleFullAddressChange('startSchedule', event.target.value)}
            error={errors.startSchedule}
            id="input-modal-endereco-data-inicio"
          />
        </Col>
        <Col md={6} xs={12} className="mb-2">
          <DatePicker
            guide
            label="Data máxima Troca Nota *"
            withTime
            value={address?.scheduledTime}
            onChange={(event) => {
              handleFullAddressChange('scheduledTime', event.target.value);
            }}
            error={errors.scheduledTime}
            id="input-modal-endereco-data-fim"
          />
        </Col>
      </Row>
    </>
  );
}

export default function SecondChangeNoteModal({ isOpen, setIsOpen }) {
  const snackbar = useSnackbar();
  const travel = useTravel();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [currency, setCurrency] = useState({
    label: 'Dólar Americano',
    value: 'USD',
  });
  const [loadValue, setLoadValue] = useState(0);
 const [errors, setErrors] = useState({});
  const [addresses, setAddresses] = useState([]);
  const [step, setStep] = useState(0);

  useEffect(() => {
    setLoadValue(travel?.data?.loads?.cargoValue || 0);
    if (travel?.data?.loads?.currency === 'BRL') {
      setCurrency({ label: 'Real Brasileiro', value: 'BRL' });
    } else {
      setCurrency({ label: 'Dólar Americano', value: 'USD' });
    }
  }, [travel?.data?.loads?.cargoValue || travel?.data?.loads?.currency]);

  function handleClose() {
    setIsOpen(false);
  }

  async function init() {
    const countriesList = await fetchCountries();
    setCountries(countriesList);
    let newAddresses = [
      {
        address: '',
        number: '',
        complement: '',
        neighborhood: '',
        zipCode: '',
        city: null,
        province: '',
        country: countries.find((country) => country.abbreviation === 'br'),
        client: null,
        lat: null,
        lng: null,
        scheduledTime: '',
        startSchedule: '',
      },
    ];

    if (travel?.data?.loads?.loadDestinations.length) {
      newAddresses = [
        ...newAddresses,
        ...travel?.data?.loads?.loadDestinations.map((address) => ({
          action: address?.action,
          address: address?.address,
          number: address?.number,
          complement: address?.complement,
          neighborhood: address?.neighborhood,
          zipCode: address?.zip_code,
          city: address?.cityModel,
          province: address?.cityModel?.province?.uf,
          country: countriesList.find(
            (country) => country.id === address?.cityModel?.province?.countryId,
          ),
          client: address?.client,
          lat: address?.lat,
          lng: address?.lng,
          scheduledTime: moment(address?.scheduledTime).format('DD/MM/YYYY HH:mm'),
          startSchedule: moment(address?.startSchedule).format('DD/MM/YYYY HH:mm'),
        })),
      ];
    }

    setAddresses(newAddresses);
    setErrors({});
    setStep(0);
  }

  useEffect(() => {
    init();
  }, [isOpen, travel?.data?.loads?.loadDestinations]);

  function toAddressPayload(address) {
    const city = typeof address?.city === 'string' ? address?.city : address?.city?.name;
    const province = typeof address?.province === 'string' ? address?.province : address?.province?.name;
    const country = typeof address?.country === 'string' ? address?.country : address?.country?.name;
    return {
      action: address?.action,
      address: address.address,
      number: address.number,
      complement: address.complement,
      zipCode: address.zipCode,
      province: address.province,
      lat: address.lat,
      lng: address.lng,
      scheduledTime: moment(address.scheduledTime, 'DD/MM/YYYY HH:mm', true).toISOString(),
      cityId: address.city?.id,
      city: city,
      neighborhood: address.neighborhood,
      formattedAddress: `${address.address} - ${address.neighborhood}, ${city || ''} - ${province || ''}, ${address.zipCode || ''}, ${country || ''}`,
      countrySlug: address.country?.abbreviation,
      startSchedule: moment(address.startSchedule, 'DD/MM/YYYY HH:mm', true).toISOString(),
      clientId: address.client?.id || null,
    };
  }

  async function onSave() {
    const [origin, ...destinations] = addresses;
    const requestData = {
      cargoValue: loadValue,
      currency: currency.value,
      origin: toAddressPayload(origin),
      destinations: destinations.map(toAddressPayload),
    };

    try {
      setIsLoading(true);
      await assertAttendanceAccess();
      const response = await cooperplaceApi.post(
        `/travels/${travel?.data?.id}/replacement`,
        requestData,
      );

      snackbar.show(<Text>Troca de Nota realizada com sucesso!</Text>, {
        type: 'success',
      });
      navigate(`/viagens/${response.data.id}`);
      handleClose();
    } catch (error) {
      let message = <Text>{error.message || 'Erro ao realizar troca de nota.'}</Text>;
      if (error.response?.data?.message) {
        message = <Text>{error.response?.data?.message}</Text>;
      } else if (error.response?.data?.errors?.length > 0) {
        message = error.response.data.errors.map((err) => (
          <>
            <Text>{err}</Text>
            <br />
          </>
        ));
      }

      snackbar.show(message, {
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }

  async function validateAddress(index, address) {
    setErrors({});
    let errorList = {};
    try {
      await addressSchema.validate(address, { abortEarly: false });
      return true;
    } catch (err) {
      if (err.inner) {
        errorList = err.inner.reduce((acc, error) => {
          acc[error.path] = error.message;
          return acc;
        }, {});

        setErrors({
          [index]: errorList,
        });
      }

      return false;
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    const currentAddress = addresses[step];
    if (!(await validateAddress(step, currentAddress))) {
      return;
    }

    const startSchedule = moment(currentAddress.startSchedule, 'DD/MM/YYYY HH:mm');

    const scheduledTime = moment(currentAddress.scheduledTime, 'DD/MM/YYYY HH:mm');

    if (!startSchedule.isValid()) {
      setErrors({
        [step]: {
          startSchedule: 'Data de início inválida',
        },
      });
      return;
    }

    if (!scheduledTime.isValid()) {
      setErrors({
        [step]: {
          scheduledTime: 'Data máxima inválida',
        },
      });
      return;
    }

    if (startSchedule.isBefore(moment())) {
      setErrors({
        [step]: {
          startSchedule: 'Data de início não pode ser menor que a data atual',
        },
      });
      return;
    }

    if (scheduledTime.isBefore(moment())) {
      setErrors({
        [step]: {
          scheduledTime: 'Data máxima não pode ser menor que a data atual',
        },
      });
      return;
    }

    if (startSchedule.isAfter(scheduledTime)) {
      setErrors({
        [step]: {
          startSchedule: 'Data de início não pode ser maior que a data máxima',
          scheduledTime: 'Data máxima não pode ser menor que a data de início',
        },
      });
      return;
    }

    if (step > 0) {
      const previousAddress = addresses[step - 1];
      const previousScheduledTime = moment(previousAddress.scheduledTime, 'DD/MM/YYYY HH:mm');

      if (startSchedule.isBefore(previousScheduledTime)) {
        setErrors({
          [step]: {
            startSchedule: 'Data de início não pode ser menor que a anterior',
          },
        });
        return;
      }

      if (scheduledTime.isBefore(previousScheduledTime)) {
        setErrors({
          [step]: {
            scheduledTime: 'Data máxima não pode ser menor que a anterior',
          },
        });
        return;
      }
    }

    if (step < addresses.length - 1) {
      setStep(step + 1);
    } else {
      onSave();
    }
  }

  function onPrev() {
    setStep(step > 0 ? step - 1 : 0);
  }

  const conversion = useMemo(() => {
    if (currency.value === 'BRL') {
      return currencyWithSymbol.find((item) => item.currency === 'USD');
    }

    return currencyWithSymbol.find((item) => item.currency === 'BRL');
  }, [currency.value]);

  const operation = useMemo(() => {
    return conversion?.symbol === 'R$' ? '*' : '/';
  }, [conversion]);

  return (
    <Modal
      heading={
        <Text color="dark" type="header">
          {step === 0 ? 'Endereço Troca de nota' : `Endereço de Destino ${step}`}
        </Text>
      }
      size="lg"
      show={isOpen}
      handleClose={handleClose}
      body={
        <form onSubmit={onSubmit}>
          <Row className="px-2 mb-2">
            <Col md={4} xs={6}>
              <Select
                label="Moeda valor da carga"
                options={currencyOptions}
                value={currency}
                onChange={(e) => setCurrency(e)}
                getOptionLabel={(option) => option?.label}
                getOptionValue={(option) => option?.value}
                id="select-moeda"
              />
            </Col>
            <Col md={4} xs={6}>
              <Input
                label={`Valor da carga ${
                  currency?.value === 'BRL' ? '(R$)' : currency?.value === 'USD' ? '($)' : ''
                }`}
                value={loadValue}
                onChange={(e) => setLoadValue(e.target.value)}
                type="number"
                id="input-valor-carga"
              />
              {loadValue > 0 ? (
                <Text type="label" color="black" weight="bold" className="mt-2">
                  Convertido:{' '}
                  {formatCurrency(
                    makeConversion(loadValue, operation, travel?.data?.loads?.exchange),
                    conversion.currency,
                    conversion.symbol,
                  )}
                </Text>
              ) : null}
            </Col>
          </Row>
          {addresses.map(
            (address, index) =>
              index === step && (
                <Address
                  key={address.id}
                  countries={countries}
                  errors={errors[index] || {}}
                  address={address}
                  onChange={(value) => {
                    const newAddresses = [...addresses];
                    newAddresses[index] = value;
                    setAddresses(newAddresses);
                  }}
                />
              ),
          )}
          <Col className="d-flex text-center justify-content-end mt-4">
            {step > 0 && (
              <Button
                variant="secondary"
                id="button-modal-endereco-salvar"
                type="submit"
                className="py-2 mr-2"
                onClick={onPrev}
                loading={isLoading}
              >
                <Text type="regular" weight="500">
                  Voltar
                </Text>
              </Button>
            )}
            <Button
              id="button-modal-endereco-salvar"
              type="submit"
              className="py-2 ms-2"
              onClick={onSubmit}
              loading={isLoading}
            >
              <Text type="regular" weight="500">
                {step < addresses.length - 1 ? 'Próximo' : 'Salvar'}
              </Text>
            </Button>
          </Col>
        </form>
      }
    />
  );
}
